// /app/frontend/src/pages/seo/GuidesHubPage.jsx
// Resources / Guides hub. Index of all SEO guide content for both
// humans (discoverability) and Google (link-equity distribution).
import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import {
  SEOHead,
  organizationSchema,
  buildBreadcrumbSchema,
  GradientHero,
} from './SEOUtils';

const SECTIONS = [
  {
    title: 'Sister Wife & Plural Family Building',
    description: 'Practical guides for couples and singles exploring intentional plural family structures.',
    items: [
      { to: '/how-to-find-a-sister-wife', label: 'How To Find A Sister Wife', body: 'A step-by-step, respectful guide to building a plural family.', status: 'live' },
      { to: '/sister-wife-dating-app', label: 'Sister Wife Dating App', body: 'How UnityLove supports sister-wife connections.', status: 'live' },
      { to: '/black-sister-wife-dating', label: 'Black Sister Wife Dating', body: 'A community for Black plural family-building.', status: 'live' },
      { to: '/sister-wives-and-poly-family-building', label: 'Sister Wives & Poly Family Building', body: 'Healthy structures for plural families.', status: 'live' },
      { to: '/building-a-polygamous-family', label: 'Building A Polygamous Family', body: 'Communication, trust, parenting, and shared values.', status: 'live' },
      { to: '/poly-family-planning', label: 'Poly Family Planning', body: 'Family planning across plural structures.', status: 'live' },
    ],
  },
  {
    title: 'Poly & Intentional Relationships',
    description: 'For couples and singles practicing or exploring ethical non-monogamy.',
    items: [
      { to: '/poly-dating-for-couples', label: 'Poly Dating For Couples', body: 'For established couples exploring ENM together.', status: 'live' },
      { to: '/ethical-polyamory-for-beginners', label: 'Ethical Polyamory For Beginners', body: 'The foundations of consent, communication, and care.', status: 'live' },
      { to: '/safe-poly-dating', label: 'Safe Poly Dating Guide', body: 'Practical safety tips for healthy poly relationships.', status: 'live' },
      { to: '/dating-as-a-couple', label: 'Dating As A Couple', body: 'Navigate shared dating experiences together.', status: 'live' },
      { to: '/dating-app-for-intentional-relationships', label: 'Dating App For Intentional Relationships', body: 'For people looking for more than casual.', status: 'live' },
    ],
  },
  {
    title: 'Community & Identity',
    description: 'Culture-first dating across the global Black community.',
    items: [
      { to: '/african-diaspora-dating', label: 'African Diaspora Dating', body: 'Connections across African Americans, Africans, Afro-Latinos, and Caribbean communities.', status: 'live' },
      { to: '/black-love-dating-app', label: 'Black Love Dating App', body: 'Meaningful relationships built on shared values.', status: 'live' },
      { to: '/verified-dating-app', label: 'Verified Dating App', body: 'How verification creates safer connections.', status: 'live' },
      { to: '/ai-matchmaking-app', label: 'AI Matchmaking App', body: 'How AI-assisted matching works at UnityLove.', status: 'live' },
    ],
  },
  {
    title: 'Local Communities',
    description: 'Find your people in cities that shaped Black love.',
    items: [
      { to: '/atlanta-black-dating', label: 'Atlanta Black Dating', body: 'Meaningful connection across the ATL.', status: 'live' },
      { to: '/houston-black-dating', label: 'Houston Black Dating', body: 'Building relationships in H-Town.', status: 'live' },
      { to: '/montgomery-dating-app', label: 'Montgomery Dating', body: 'Dating in Montgomery and the River Region.', status: 'live' },
    ],
  },
];

export default function GuidesHubPage() {
  const path = '/guides';
  const schemas = [
    organizationSchema,
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Guides', url: path },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="UnityLove Guides & Resources"
        description="Browse all UnityLove guides on intentional relationships, poly family-building, sister-wife structures, and culture-first community dating."
        canonical={path}
        schemas={schemas}
      />

      <GradientHero
        eyebrow="Resources"
        headline="UnityLove Guides & Resources"
        sub="A growing library of honest, practical writing on intentional relationships, plural family-building, and the realities of dating across the Black diaspora."
        primaryCta={{ label: 'Create Your Profile Free', to: '/auth?mode=signup' }}
        secondaryCta={null}
      />

      <main className="max-w-4xl mx-auto px-4 py-14">
        {SECTIONS.map((section) => (
          <section key={section.title} className="mb-14">
            <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-2">
              {section.title}
            </h2>
            <p className="text-slate-600 mb-6">{section.description}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {section.items.map((item) => (
                <a
                  key={item.to}
                  href={item.status === 'live' ? item.to : undefined}
                  className={`block rounded-2xl border p-5 transition-all ${
                    item.status === 'live'
                      ? 'border-slate-200 bg-white hover:border-rose-400 hover:shadow-sm cursor-pointer'
                      : 'border-slate-100 bg-slate-50 cursor-default'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className={`font-semibold ${item.status === 'live' ? 'text-slate-900' : 'text-slate-500'}`}>
                      {item.label}
                    </p>
                    {item.status === 'coming-soon' && (
                      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">
                        Coming soon
                      </span>
                    )}
                  </div>
                  <p className={`text-sm mt-1.5 ${item.status === 'live' ? 'text-slate-600' : 'text-slate-400'}`}>
                    {item.body}
                  </p>
                </a>
              ))}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}
